import { Radio, RadioGroup, Spacing } from '@billy/design-system'
import { ChangeEvent, ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { InvoiceDiscountMode } from '@features/invoices/enums/invoiceDiscountMode'
import { getDiscountModeTrackingValue } from '@features/invoices/utils/getDiscountModeTrackingValue'
import { isInvoiceDiscountMode } from '@features/invoices/utils/isInvoiceDiscountMode'

import { Scope } from '../../../../../../../../../../../../../../enums/scope'
import { isAuthorized } from '../../../../../../../../../../../../../../utils/isAuthorized'
import { useInvoiceEditorForm } from '../../../../../../../../../../contexts/invoiceEditorFormContext'
import { useUpdateDependentLineValues } from '../../../../../../hooks/useUpdateDependentLineValues'
import * as Styles from './styles'

interface InvoiceDiscountsOptionsProps {
  onUpgradeRequired: () => void
}

export const InvoiceDiscountsOptions = ({ onUpgradeRequired }: InvoiceDiscountsOptionsProps): ReactElement => {
  const { t } = useTranslation()
  const { discountMode, setDiscountMode, invoiceEditorForm, trackInvoiceEditor } = useInvoiceEditorForm()
  const { handleClearLineDiscounts } = useUpdateDependentLineValues()

  const isLockedByUpsell = isAuthorized(Scope.InvoiceDiscountUpsell)

  const { setValue } = invoiceEditorForm

  const discountModeOptions = useMemo(
    () => [
      {
        value: InvoiceDiscountMode.None,
        label: t('external_invoices.editor.form.field.invoice_lines.discounts.mode.none'),
      },
      {
        value: InvoiceDiscountMode.ByItem,
        label: t('external_invoices.editor.form.field.invoice_lines.discounts.mode.by_item'),
      },
      {
        value: InvoiceDiscountMode.Global,
        label: t('external_invoices.editor.form.field.invoice_lines.discounts.mode.global'),
      },
    ],
    [t],
  )

  const handleDiscountModeChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget
      if (!isInvoiceDiscountMode(value)) {
        return
      }

      if (isLockedByUpsell && value !== InvoiceDiscountMode.None) {
        onUpgradeRequired()
        return
      }

      if (value !== InvoiceDiscountMode.ByItem) {
        handleClearLineDiscounts()
      }

      if (value !== InvoiceDiscountMode.Global) {
        setValue('discount', 0, { shouldDirty: true })
      }

      setDiscountMode(value)
      trackInvoiceEditor('Discount mode selected', { discountMode: getDiscountModeTrackingValue(value) })
    },
    [handleClearLineDiscounts, onUpgradeRequired, setDiscountMode, setValue, isLockedByUpsell, trackInvoiceEditor],
  )

  return (
    <Styles.InvoiceDiscountsOptionsWrapper>
      <RadioGroup onChange={handleDiscountModeChange} value={discountMode} direction="column" spacing={Spacing.S16}>
        {discountModeOptions.map(({ value, label }) => (
          <Radio key={value} value={value}>
            {label}
          </Radio>
        ))}
      </RadioGroup>
    </Styles.InvoiceDiscountsOptionsWrapper>
  )
}
